import React, { createContext, useContext, useState, useCallback } from 'react';

export interface BreadcrumbItem {
    label: string;
    path: string;
    state?: unknown;
}

interface BreadcrumbContextType {
    items: BreadcrumbItem[];
    currentPage: string | null;
    /**
     * Browser-tab/history spelling of the current page when it should differ
     * from the TopBar crumb. Drill-ins use the bare RO / invoice number.
     */
    documentTitle: string | null;
    setBreadcrumbs: (items: BreadcrumbItem[], currentPage?: string, documentTitle?: string) => void;
    clearBreadcrumbs: () => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType>({
    items: [],
    currentPage: null,
    documentTitle: null,
    setBreadcrumbs: () => {},
    clearBreadcrumbs: () => {},
});

export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<BreadcrumbItem[]>([]);
    const [currentPage, setCurrentPage] = useState<string | null>(null);
    const [documentTitle, setDocumentTitle] = useState<string | null>(null);

    const setBreadcrumbs = useCallback((newItems: BreadcrumbItem[], page?: string, title?: string) => {
        setItems(newItems);
        setCurrentPage(page ?? null);
        setDocumentTitle(title ?? null);
    }, []);

    const clearBreadcrumbs = useCallback(() => {
        setItems([]);
        setCurrentPage(null);
        setDocumentTitle(null);
    }, []);

    return (
        <BreadcrumbContext.Provider value={{ items, currentPage, documentTitle, setBreadcrumbs, clearBreadcrumbs }}>
            {children}
        </BreadcrumbContext.Provider>
    );
};

export const useBreadcrumbs = () => useContext(BreadcrumbContext);

export default BreadcrumbContext;
